import {
  loopsOnLayer,
  selectionOf,
  summarizeSelection,
} from '@frontend/viewer-core';

import type { DrawingModel, DxfViewerSession } from '../session/types';
import { useReadableStore } from '../session/useSessionState';
import {
  formatArea,
  formatLength,
  formatSize,
  type MeasurementFormat,
} from '../utils/formatMeasurement';

export type DxfDetailsOverlayState = {
  size: string;
  hasDetail: boolean;
  heading: string;
  count: number;
  length: string;
  area: string | null;
};

/**
 * Connects `DxfDetailsOverlay` to the session. Call only under a ready drawing —
 * empty bounds returns `null` so the overlay can hide, which is not a load state.
 */
export const useDxfDetailsOverlay = (
  session: DxfViewerSession,
  model: DrawingModel,
  format: MeasurementFormat,
): DxfDetailsOverlayState | null => {
  const selection = useReadableStore(session.selection, (current) => current);
  const hoveredLayer = useReadableStore(
    session.hoveredLayer,
    (current) => current,
  );

  const bounds = model.bounds;
  if (!bounds) {
    return null;
  }

  const [minX, minY, maxX, maxY] = bounds;

  const hoveredSummary =
    hoveredLayer === null
      ? null
      : summarizeSelection(
          selectionOf(loopsOnLayer(model.loops, hoveredLayer)),
          model.loops,
        );

  // A hovered layer takes precedence: the user is asking about it right now.
  const summary = hoveredSummary ?? summarizeSelection(selection, model.loops);
  const heading =
    hoveredLayer === null
      ? 'Selection'
      : model.scene.layers[hoveredLayer]?.name || 'Layer';
  const hasDetail = summary.count > 0;

  return {
    size: formatSize(maxX - minX, maxY - minY, format),
    hasDetail,
    heading,
    count: summary.count,
    length: hasDetail ? formatLength(summary.length, format) : '',
    area:
      hasDetail && summary.area !== null
        ? formatArea(summary.area, format)
        : null,
  };
};
